import { useParams } from "@reach/router"
import { FORM_ERROR } from "final-form"
import { graphql, navigate, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Fragment, useEffect, useState } from "react"
import { Field, Form } from "react-final-form"

import {
  ABTestContainer,
  AB_TESTS,
  isABTestActive,
} from "@trueskin-web/ab-experiments"
import { marketingClient } from "@trueskin-web/apis"
import {
  ALink,
  Button,
  CheckboxControl,
  EmailAutocomplete,
  FieldError,
  Heading,
  Icon,
  Seo,
  Spinner,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import {
  composeValidators,
  getSignUpError,
  mustBeEmail,
  required,
} from "@trueskin-web/functions"
import {
  getFormValidationErrors,
  getServerValidationErrorTypes,
} from "@trueskin-web/functions"
import { getCountry } from "@trueskin-web/locales"
import { trackingService } from "@trueskin-web/services"
import { getStorageKeys } from "@trueskin-web/services/src/storage"
import { arrowLongRightIcon } from "@trueskin-web/theme/icons/arrowLongRightIcon"
import {
  Routes,
  Slugs,
  Trans,
  useTranslation,
} from "@trueskin-web/translations"

import useFocus from "../../hooks/use-focus"
import SocialSignUp from "../../social/signup"

import domains from "./domains"
import ExistingAccountDialog from "./existing-account-dialog"

const storageKeys = getStorageKeys()

const CreateAccount = () => {
  const { t } = useTranslation()

  const { token } = useParams()

  const searchParams = new URLSearchParams(location.search)

  const email = searchParams.get("email") || ""
  const provider = searchParams.get("provider") || ""
  const providerID = searchParams.get("providerID") || ""
  const isLoggingIn = searchParams.has(storageKeys.AUTH_JWT)
  const agreeTerms = provider === "apple"
  let submit

  const [focusRef, setFocusRef] = useFocus()

  const [triedSocialSignUp, setTriedSocialSignUp] = useState(false)

  const { heroImage, loginFormImage } = useStaticQuery(graphql`
    {
      heroImage: file(relativePath: { eq: "signup_hero.webp" }) {
        childImageSharp {
          gatsbyImageData(width: 1120, layout: CONSTRAINED)
        }
      }
      loginFormImage: file(
        relativePath: { eq: "rebranded/rebranded-login-form.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 800, layout: CONSTRAINED)
        }
      }
    }
  `)

  const { register } = useAuth()

  const [showExistingAccountDialog, setShowExistingAccountDialog] =
    useState(false)

  const socialAuthExperimentActive = isABTestActive(AB_TESTS.SocialAuth)

  useEffect(() => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.SIGN_UP_OPENED,
      data: {
        provider,
        temptoken: token,
      },
    })
  }, [token, provider])

  useEffect(() => {
    // disable focus on autofill when social auth experiment is active
    if (socialAuthExperimentActive) return
    const timerId = setTimeout(() => {
      if (
        document.querySelector("input:-webkit-autofill") !== focusRef.current
      ) {
        setFocusRef()
      }
    }, 700)

    return () => clearTimeout(timerId)
  }, [focusRef, setFocusRef, socialAuthExperimentActive])

  const handleSubmit = async (values) => {
    try {
      await register({
        email: values.email,
        provider,
        providerID,
        signedUpWithEmailOnly: true,
        newsletterSubscription: values.newsletterSubscription,
        tempToken: token,
        country: getCountry(),
        campaignTracking: trackingService.getTrackingTouchpoints(),
        touchPoints: trackingService.getAllTrackingTouchPoints(),
      })

      trackingService.clearTrackingTouchpoints()

      navigate(Routes.App.Start.url, {
        replace: true,
      })
    } catch (error) {
      handleSubmitError(error)

      return {
        [FORM_ERROR]: getSignUpError({
          error,
          action: handleClickLoginLink,
          cta: Button,
        }),
      }
    }
  }

  const handleFormValidationErrors = (errors) => {
    const errorTypes = getFormValidationErrors(errors)

    errorTypes.forEach(trackCreateAccountError)
  }

  const handleSubmitError = (error) => {
    if (error.USERNAME_OR_EMAIL_TAKEN) {
      const errorTypes = getServerValidationErrorTypes()

      trackCreateAccountError(errorTypes.email_existing)

      setShowExistingAccountDialog(true)
    }
  }

  const handleClickLoginLink = () => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.LOGIN_FROM_ERROR_MESSAGE,
    })

    Object.assign(document.createElement("a"), {
      href: Routes.App.Login.url,
    }).click()
  }

  const trackCreateAccountError = (errorType) =>
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.ACCOUNT_CREATION_ERROR,
      data: {
        errorType,
      },
    })

  // auto submit if user just signed in with apple
  useEffect(() => {
    if (provider === "apple" && providerID && submit && !isLoggingIn) {
      submit()
    }
  }, [provider, submit])

  // show spinner if we just received an auth token
  if (isLoggingIn) {
    return <Spinner isFullPage disableScroll />
  }
  return (
    <Fragment>
      <Seo title={t("Start.CreateAccount.seoTitle")} />

      <div
        data-component-name="StartCreateAccount"
        sx={{
          mx: [-4, 0],
          variant: "vwo.page",
        }}
      >
        <Form
          initialValues={{
            email,
            agreeTerms,
            newsletterSubscription: false,
          }}
          onSubmit={handleSubmit}
          render={({
            handleSubmit,
            form,
            values,
            submitting,
            errors,
            submitError,
            hasValidationErrors,
            dirtySinceLastSubmit,
          }) => {
            submit = handleSubmit
            return (
              <form
                data-testid="web-portal-create-account-form"
                onSubmit={(event) => {
                  if (hasValidationErrors) {
                    handleFormValidationErrors(errors)
                  }
                  setTriedSocialSignUp(false)
                  handleSubmit(event)
                }}
                noValidate
                sx={{
                  display: "grid",
                  alignItems: "end",
                  gridTemplateColumns: ["16px 5fr 3fr 16px", "1fr 2fr 2fr 1fr"],
                }}
              >
                <GatsbyImage
                  image={getImage(heroImage)}
                  alt=""
                  objectPosition="center top"
                  sx={{
                    minHeight: [228, "auto"],
                    gridColumn: "1 / 5",
                    gridRow: ["1 / 4", "1 / 7"],
                    zIndex: -1,
                  }}
                />

                <Heading
                  as="h1"
                  sx={{
                    gridColumn: "2 / 3",
                    gridRow: "2 / 3",
                    mt: [0, 4],
                    fontSize: 2,
                    fontWeight: "bold",
                    lineHeight: "body",
                    letterSpacing: "0.02em",
                  }}
                >
                  <Trans i18nKey="Start.CreateAccount.title" />
                </Heading>

                <div
                  sx={{
                    gridColumn: "2 / 3",
                    gridRow: "1 / 2",
                    mt: [0, 7],
                    fontSize: 1,
                  }}
                >
                  <Trans i18nKey="Start.CreateAccount.description" />
                </div>

                <div
                  sx={{
                    gridColumn: ["2 / 4", "2 / 3"],
                    gridRow: ["3 / 4", "6 / 7"],
                    mt: [1, 0],
                    mb: [1, 4],
                    mr: [0, -10],
                    fontSize: 0,
                  }}
                >
                  <Trans i18nKey="Start.CreateAccount.details" />
                </div>

                <div
                  sx={{
                    gridColumn: ["2 / 4", "2 / 3"],
                    gridRow: ["4 / 5", "3 / 4"],
                    mt: 5,
                    input: {
                      backgroundColor: [null, "white"],
                    },
                  }}
                >
                  <EmailAutocomplete
                    ref={focusRef}
                    domains={domains}
                    label={t("Start.CreateAccount.email")}
                    desktopErrorMessagePlaceholder
                    validate={
                      !triedSocialSignUp
                        ? composeValidators(required, mustBeEmail)
                        : () => true
                    }
                    isDisabled={submitting}
                    onInputChange={(value) => form.change("email", value)}
                  />

                  <Button
                    data-testid="web-portal-create-account-submit"
                    type="submit"
                    isLoading={submitting}
                    size="sm"
                    isFullWidth
                    rounded
                    sx={{
                      mt: [5, 0],
                      fontSize: 1,
                      fontWeight: "bold",
                    }}
                  >
                    <Trans i18nKey="Start.CreateAccount.submitButton" />
                    <Icon
                      icon={arrowLongRightIcon}
                      sx={{
                        position: "relative",
                        top: "2px",
                        my: "1px",
                        ml: 1,
                      }}
                    />
                  </Button>

                  {submitError && !dirtySinceLastSubmit && (
                    <FieldError>{submitError}</FieldError>
                  )}

                  <ABTestContainer
                    testName={AB_TESTS.SocialAuth}
                    variants={[[1, SocialSignUp]]}
                    disabled={!values.agreeTerms}
                    onSubmitDisabled={() => {
                      setTriedSocialSignUp(true)
                    }}
                  />
                </div>

                <Field
                  dataTestId="web-portal-agree-terms"
                  name="agreeTerms"
                  isInvalid={triedSocialSignUp && !values.agreeTerms}
                  component={CheckboxControl}
                  size={["md", "sm"]}
                  validate={(value) =>
                    value
                      ? undefined
                      : t("Start.CreateAccount.mandatoryFieldError")
                  }
                  mobileErrorMessagePosition="top"
                  desktopErrorMessagePosition="top"
                  type="checkbox"
                  rounded={true}
                  sx={{
                    gridColumn: "2 / 4",
                    gridRow: ["8 / 9", "7 / 8"],
                    mt: 5,
                    "& > label > div": {
                      fontSize: 0,
                    },
                  }}
                >
                  Ich bin einverstanden mit den{" "}
                  <ALink
                    href={Slugs["terms-and-conditions"].url}
                    target="_blank"
                    isUnderlined
                    sx={{ fontWeight: "bold" }}
                  >
                    AGB
                  </ALink>
                  , der{" "}
                  <ALink
                    href={Slugs["terms-and-conditions"].url}
                    target="_blank"
                    isUnderlined
                    sx={{ fontWeight: "bold" }}
                  >
                    Widerrufsbelehrung
                  </ALink>{" "}
                  und dem{" "}
                  <ALink
                    href={Slugs["data-protection"].url}
                    target="_blank"
                    isUnderlined
                    sx={{ fontWeight: "bold" }}
                  >
                    Datenschutzhunweis
                  </ALink>
                  . Ich willige ein in die Verarbeitung personenbezogener Daten
                  (einschließlich Gesundheitsdaten) durch die FORMEL SKIN Gruppe
                  und die ausgewählte Apotheke für die Zwecke der
                  Leistungserbringung, und die Abrechnung über die FORMEL SKIN
                  Derma GmbH, der mein Arzt meine Rechnungsdaten (ohne Befunde)
                  übermitteln darf.
                </Field>

                {showExistingAccountDialog ? (
                  <ExistingAccountDialog
                    email={values.email}
                    image={loginFormImage}
                    onDismiss={() => setShowExistingAccountDialog(false)}
                  />
                ) : null}
              </form>
            )
          }}
        />
      </div>
    </Fragment>
  )
}

export default CreateAccount
